import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { cn } from '@/utils/classUtils';
import { SupportedRegion } from '@ditto/shared';
import {
  ArrowUpRight,
  Calendar,
  ChevronDown,
  ClipboardCheck,
  FileText,
  Package,
  Scale,
  Stethoscope,
  Target,
  TrendingUp,
  X,
} from 'lucide-react';
import { useState, type FC } from 'react';

interface QueryWithRegion {
  query: string;
  region: SupportedRegion;
}

interface Capability {
  id: string;
  title: string;
  icon: FC<{ className?: string }>;
  queries: QueryWithRegion[];
  onDismiss?: (id: string) => void;
}

interface QueryButtonProps {
  query: string;
  onClick: (query: string, region: SupportedRegion) => void;
  queryRegion: SupportedRegion;
}

const QueryButton: FC<QueryButtonProps> = ({
  query,
  onClick,

  queryRegion,
}) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    onClick(query, queryRegion);
  };

  return (
    <button
      onClick={handleClick}
      type="button"
      className={cn(
        'flex items-center justify-between gap-2',
        'rounded-lg border border-neutral-200 px-3 py-2',
        'text-left transition-colors',
        'text-sm text-neutral-700 hover:bg-neutral-50 w-full'
      )}
    >
      <span className="flex-1">{query}</span>
      <ArrowUpRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
    </button>
  );
};

interface CapabilityCardProps {
  capability: Capability;
  onQueryClick: (query: string, region: SupportedRegion) => void;
}

const CapabilityCard: FC<CapabilityCardProps> = ({
  capability,
  onQueryClick,
}) => {
  const { id, title, icon: Icon, queries, onDismiss } = capability;

  return (
    <FlexCol
      className={cn(
        'w-full rounded-lg border border-neutral-200 p-4 gap-3 bg-white'
      )}
    >
      <FlexRow alignItems="center" justifyContent="between">
        <FlexRow alignItems="center" gap="2">
          <Icon className="w-5 h-5 text-neutral-600" />
          <Text variant="body-sm-bold" color="primary">
            {title}
          </Text>
        </FlexRow>
        {onDismiss && (
          <button
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onDismiss(id);
            }}
            type="button"
            className="w-6 h-6 flex items-center justify-center rounded hover:bg-neutral-100 transition-colors"
          >
            <X className="w-4 h-4 text-neutral-500" />
          </button>
        )}
      </FlexRow>
      <FlexCol gap="2" className="mt-1">
        {queries.map((queryWithRegion, index) => (
          <QueryButton
            key={index}
            query={queryWithRegion.query}
            onClick={onQueryClick}
            queryRegion={queryWithRegion.region}
          />
        ))}
      </FlexCol>
    </FlexCol>
  );
};

export interface CapabilitiesProps {
  onQueryClick: (query: string, region: SupportedRegion) => void;
}

export const Capabilities: FC<CapabilitiesProps> = ({ onQueryClick }) => {
  const [selectedCapabilityId, setSelectedCapabilityId] = useState<
    string | null
  >(null);
  const [isMoreExpanded, setIsMoreExpanded] = useState(false);

  const allCapabilities: Capability[] = [
    {
      id: 'regulatory-classification',
      title: 'Regulatory Classification',
      icon: Scale,
      queries: [
        {
          query:
            'Our blood pressure cuff software shows "pre-hypertension" warnings - does this make it a diagnostic vs monitoring device for the FDA?',
          region: 'US',
        },
        {
          query:
            'Our surgical mesh predicate was reclassified to Class III by the FDA after our submission - what actions do we need to take?',
          region: 'US',
        },
        {
          query:
            'Our medical device auto-adjusts therapy based on patient data - when does FDA consider it closed-loop?',
          region: 'US',
        },
        {
          query:
            'Our combination product is 70% device, 30% drug - how can we determine if it falls under CDER or CDRH jurisdiction for the US market?',
          region: 'US',
        },
      ],
    },
    {
      id: 'regulatory-timelines',
      title: 'Regulatory Timelines',
      icon: Calendar,
      queries: [
        {
          query:
            "Our medical device has its MDD certificate expiring in 3 months but the MDR review will take 6 months - if it's unchanged, can we keep selling it?",
          region: 'EU',
        },
        {
          query:
            'Our UK-based EU authorized representative lost its status after Brexit - is there a grace period for replacement?',
          region: 'EU',
        },
      ],
    },
    {
      id: 'regulatory-requirements',
      title: 'Regulatory Requirements',
      icon: FileText,
      queries: [
        {
          query:
            'Our UK-based EU authorized representative lost its status after Brexit - is there a grace period for replacement?',
          region: 'US',
        },
        {
          query:
            'EU notified body says our FDA 510(k) testing is insufficient for MDR requirements - must we repeat all our testing?',
          region: 'WORLD',
        },
      ],
    },
    {
      id: 'quality-supplier',
      title: 'Quality Supplier',
      icon: Package,
      queries: [
        {
          query:
            'Our US supplier ISO 13485 certificate expired 6 months ago but they have a shipment already in transit from their site to ours - do we accept the shipment of components for our class II device??',
          region: 'EU',
        },
        {
          query:
            "Our contract manufacturer's employee in manufacturing our product in the US failed a drug test after making our lot - do we need to recall the lot?",
          region: 'US',
        },
        {
          query:
            'Our reference lab for the US market lost its accreditation after testing our samples - do we need to organize a retest?',
          region: 'US',
        },
      ],
    },
    {
      id: 'quality',
      title: 'Quality',
      icon: Target,
      queries: [
        {
          query:
            'Our sterilization validation used 10^-6 value but our label for the FDA claims 10^-3 - which value should we use?',
          region: 'US',
        },
        {
          query:
            'We received a customer complaint describing a problem we fixed 2 years ago but never told the FDA about - what do we need to report?',
          region: 'US',
        },
        {
          query:
            'Our batch record shows 22°C but our spec is 20-21°C while stability data supports 15-25°C for our US system - what do we need to do?',
          region: 'US',
        },
        {
          query:
            'We found an old email promising one of our customer a feature that we never actually delivered - is this considered a design control issue?',
          region: 'US',
        },
      ],
    },
    {
      id: 'post-market',
      title: 'Post-Market',
      icon: TrendingUp,
      queries: [
        {
          query:
            "One hospital in the US mixed our device with a competitor's device for one patient - which company needs to consider this an adverse event?",
          region: 'US',
        },
        {
          query:
            'Our device was used in a homemade ventilator during COVID by a hospital in the US - is this case considered a liability for off-label use?',
          region: 'US',
        },
        {
          query:
            'Our post-market study shows US doctors prefer our device for a different indication than originally labelled - do we need to issue a labeling update?',
          region: 'US',
        },
        {
          query:
            'We found a patient group on Reddit reporting an issue we had not previously seen - how do we need to investigate the issue for the FDA?',
          region: 'US',
        },
        {
          query:
            'Our device worked without malfunction but one patient in the US sued anyway, solely claiming psychological anxiety - is this considered a reportable event?',
          region: 'US',
        },
        {
          query:
            'We have found literature reports explaining new biocompatibility concern for material we have used for the past 10 years in our US product- what do we need to do?',
          region: 'US',
        },
        {
          query:
            'Our distributor modified our medical device software for local language in the EU without telling us - are we responsible for the risk generated?',
          region: 'EU',
        },
        {
          query:
            'Clinical data shows ethnic differences in our device effectiveness - do we need to stratify labeling for the FDA?',
          region: 'US',
        },
        {
          query:
            'MAUDE database shows competitor issues but the EU database does not - what do we need to reference in our clinical evaluation for MDR?',
          region: 'WORLD',
        },
        {
          query:
            'A patient got injured in France but was treated in the US - which reporting timeline applies in this case?',
          region: 'WORLD',
        },
      ],
    },
    {
      id: 'audit-inspection',
      title: 'Audit and Inspection',
      icon: ClipboardCheck,
      queries: [
        {
          query:
            'Notified body cites an ISO clause that conflicts with FDA requirements - what requirement do we need to follow?',
          region: 'WORLD',
        },
        {
          query:
            'The FDA wants annual reports but the EU require semi-annual reports - can we combine our reports to answer both requirements?',
          region: 'WORLD',
        },
        {
          query:
            'The FDA issued a Form 483 where the observations conflict with our more recent EU certificate - do we have to mention this to the FDA?',
          region: 'WORLD',
        },
        {
          query:
            'The FDA is asking us about our EU vigilance reports that were not filed in US - do we need to explain threshold differences?',
          region: 'WORLD',
        },
        {
          query:
            'A mock FDA-like inspection found issues that our EU audits missed - do we need to self-report to the notified body?',
          region: 'WORLD',
        },
        {
          query:
            'Notified body wants to conduct an unannounced audit during our August holiday facility shutdown - can we negotiate timing?',
          region: 'EU',
        },
        {
          query:
            'German authority (BfArM) wants to conduct an inspection but we just had a notified body audit - is the scope of the inspection different?',
          region: 'EU',
        },
        {
          query:
            'The auditor cites guidance document MDCG 2019-6 for nonconformity - are MDCG guidances considered mandatory?',
          region: 'EU',
        },
        {
          query:
            'We have a remote audit scheduled but the auditor wants a live warehouse tour - can this be challenged citing a technical feasibility issue?',
          region: 'US',
        },
        {
          query:
            "The notified body auditor requests access to our economic operator's QMS - is this considered a contractual requirement?",
          region: 'EU',
        },
        {
          query:
            'We have an ISO 13485 certificate from a different notified body than our MDR certificate - is this acceptable?',
          region: 'EU',
        },
        {
          query:
            'An auditor has identified "minor" finding that seems major to us - should we appeal during the audit or after?',
          region: 'EU',
        },
        {
          query:
            'The notified body wants to charge extra for reviewing Class IIa technical documentation - is this considered normal practice?',
          region: 'EU',
        },
      ],
    },
    {
      id: 'technical-documentation',
      title: 'Technical Documentation',
      icon: FileText,
      queries: [
        {
          query:
            'The IFU that were approved for CE marking contain claims the FDA might consider promotional - do we need to have two separate versions for the EU and the US?',
          region: 'WORLD',
        },
        {
          query:
            "Our EU technical file includes competitor predicate data that the FDA won't accept - do we need to maintain two versions of our technical files?",
          region: 'WORLD',
        },
        {
          query:
            'The device name that was cleared by the FDA does not match our EU trade name - do we need to harmonize?',
          region: 'WORLD',
        },
        {
          query:
            'Our FDA label requires "Rx only" but our EU label requires OTC availability - do we need to have dual labeling in place?',
          region: 'WORLD',
        },
        {
          query:
            'Our technical file is in English only but we are selling the device in 12 EU countries - must we translate everything or just specific sections?',
          region: 'EU',
        },
        {
          query:
            'GSPR checklist references harmonized standards from 2018 but newer versions exist - are we required to update our existing certificate to take newer standards into account?',
          region: 'EU',
        },
        {
          query:
            'Our IFU must fit 24 EU languages on a credit-card sized label - are we allowed to use symbols only and provide a separate booklet?',
          region: 'EU',
        },
        {
          query:
            'Our device has 200+ variants (sizes/configurations) - does our technical file need separate verification data for each variant under MDR?',
          region: 'EU',
        },
        {
          query:
            'Notified body wants technical documentation in their specific format and not in the MDR Annex II structure - do we have to accept their demands or can we challenge?',
          region: 'EU',
        },
      ],
    },
    {
      id: 'clinical-evaluation',
      title: 'Clinical Evaluation',
      icon: Stethoscope,
      queries: [
        {
          query:
            'Our CER references 10-year-old clinical data - when is data too old for MDR acceptance?',
          region: 'EU',
        },
        {
          query:
            "The equivalent device manufacturer won't share clinical data - can we use published literature only in our evaluation under MDR?",
          region: 'EU',
        },
        {
          query:
            'Our PMCF plan under MDR requires to use a registry but no suitable EU registry exists - are we allowed and should we create our own?',
          region: 'EU',
        },
        {
          query:
            'During our clinical evaluation we consulted 500 literature articles but ended up citing only 12 - is there a minimum number of results expected under MDR?',
          region: 'EU',
        },
        {
          query:
            'The notified body wants a prospective clinical investigation for our Class IIa device with 20 years of market EU presence - is their demand justified?',
          region: 'EU',
        },
        {
          query:
            'Our US clinical trial had different endpoints than the ones the EU requires - is the clinical trial data still usable for our CER?',
          region: 'EU',
        },
        {
          query:
            'Our CER author left the company before signing off on the final version - under MDR, who can sign the CER retroactively?',
          region: 'EU',
        },
        {
          query:
            'We are modifying our medical device, and the modification seems "significant" - do we need to provide new clinical data to the FDA or can an updated literature review be sufficient?',
          region: 'US',
        },
        {
          query:
            'We filed a vigilance report in France - must we notify all 27 member state authorities?',
          region: 'EU',
        },
        {
          query:
            'Post-market surveillance shows off-label use in 60% of cases - is an update to our risk management sufficient for FDA requirements?',
          region: 'US',
        },
        {
          query:
            'We have noticed a trend reporting threshold was reached in Germany but not EU-wide - do we report to BfArM or wait?',
          region: 'EU',
        },
        {
          query:
            'We discovered unreported 2019 incidents during the MDR transition - is retrospective reporting required?',
          region: 'EU',
        },
        {
          query:
            "Our economic operator received a complaint but didn't forward it to us for 45 days - when does our clock start to respond under MDR?",
          region: 'EU',
        },
        {
          query:
            'Our PMCF survey response rate is only 3% - is this enough for our conclusions to be statistically valid under MDR?',
          region: 'EU',
        },
        {
          query:
            'We implemented field safety corrective action in one country but authorities want EU-wide recall - is their request disproportionate?',
          region: 'EU',
        },
        {
          query:
            'Our QMR is based in our US office - is this acceptable for EU MDR requirements or do we absolutely need someone EU-based?',
          region: 'EU',
        },
        {
          query:
            'We have combined procedures for FDA/EU requirements but our notified body wants an EU-specific procedure - is this really necessary or can we push back on their request?',
          region: 'EU',
        },
        {
          query:
            'The Person Responsible for Regulatory Compliance (PRRC) in the EU is leaving the company in 2 weeks - how long do we have to replace them?',
          region: 'EU',
        },
        {
          query:
            'Our Spanish facility handles final testing, but Italian facility is the one doing the labeling - which is considered the "manufacturer" for MDR?',
          region: 'EU',
        },
        {
          query:
            'Our management review includes global data - must we separate EU-specific metrics?',
          region: 'EU',
        },
        {
          query:
            'Our CAPA system allows for 90-day extensions but notified body says 60 is the maximum - which one prevails?',
          region: 'EU',
        },
        {
          query:
            'Our Post-market surveillance procedure is combined with our vigilance procedure but our notified body wants separation - is this really required?',
          region: 'EU',
        },
        {
          query:
            'Our contract manufacturer is ISO 9001 certified but not ISO 13485 - is this acceptable for non-sterile device?',
          region: 'US',
        },
        {
          query:
            'Our testing lab has an ISO 17025 accreditation that will expire next month - when must we switch labs?',
          region: 'US',
        },
        {
          query:
            'Our economic operator in the EU is the one performing final release but we want to retain legal manufacturer status - is this possible?',
          region: 'EU',
        },
        {
          query:
            'Our contract manufacturer in the US wants to subcontract to their sister company - can they do it without consulting us or is our approval needed?',
          region: 'US',
        },
        {
          query:
            'Our European authorized representative wants to include limitation of liability clauses in the contract - does this have implications for MDR?',
          region: 'EU',
        },
        {
          query:
            'The Notified body quotes that we have180 days for Class IIa review but MDR seems to indicate 90 days - which one is correct?',
          region: 'EU',
        },
        {
          query:
            'We missed the EUDAMED actor registration deadline - can we still place devices on market?',
          region: 'EU',
        },
        {
          query:
            'Our Class I device is getting upgraded to Class IIa under MDR - when must we stop self-declaring?',
          region: 'EU',
        },
        {
          query:
            'We made a substantial modification in December - when does our 120-day clock for notification start for the FDA?',
          region: 'US',
        },
        {
          query:
            'Our PSUR shows we need IFU update - do we have to implement immediately or can we wait for the next periodic review?',
          region: 'EU',
        },
        {
          query:
            'Our software calculates medication dosage but requires doctor approval to validate the updated dosage - does this make our device Class IIa or Class IIb under MDR?',
          region: 'EU',
        },
        {
          query:
            'Our device touches only intact skin but measures subcutaneous tissue - can it be considered invasive by the FDA?',
          region: 'US',
        },
        {
          query:
            'Our medical device has modular components with different classifications - how do we determine which class applies in the EU? is it the highest class?',
          region: 'EU',
        },
        {
          query:
            'Our surgical instrument is used with our active medical device but functions independently - can we classify them separately for the FDA?',
          region: 'US',
        },
        {
          query:
            'Our medical device "continuous use" is interrupted for cleaning every 8 hours - is it still considered continuous for Rule 1?',
          region: 'EU',
        },
        {
          query:
            'Our AI algorithm improves over time - does this learning capability automatically mean higher classification in the US for our medical device?',
          region: 'US',
        },
        {
          query:
            'Our competitor device has serious incident reports but a similar benefit-risk analysis than our device - can we still consider the competitor device valid as equivalent device under MDR?',
          region: 'EU',
        },
        {
          query:
            'Our legacy device lacks clinical data but has 15 years of post-market surveillance data - can this be consiedered sufficient for our initial CER under MDR?',
          region: 'EU',
        },
        {
          query:
            'Our PMCF study shows unexpected off-label use in 40% of cases - do we need to update our CER immediately or are we allowed to wait for the planned periodic review under MDR?',
          region: 'EU',
        },
        {
          query:
            'The notified body rejects our literature search methodology after our CER got approved - is a retroactive revision required?',
          region: 'EU',
        },
        {
          query:
            'Is clinical data from UK pre-2021 still valid for EU MDR submission?',
          region: 'EU',
        },
        {
          query:
            'A predicate device was withdrawn for commercial reasons in the US, not safety reasons - can we reference its clinical data?',
          region: 'US',
        },
        {
          query:
            'Our medical device treats rare disease with <50 EU patients - how can we meet statistical significance requirements for MDR?',
          region: 'EU',
        },
        {
          query:
            'Our CER demonstrates safety but limited efficacy data - is it possible to get a conditional certification while gathering PMCF data?',
          region: 'EU',
        },
        {
          query:
            'Our device software update improves accuracy by 15% - is a new clinical evaluation required or can an addendum be sufficient for the FDA?',
          region: 'US',
        },
        {
          query:
            'A substantially equivalent device has different contraindications - what is the impact on clinical data acceptability to the FDA?',
          region: 'US',
        },
      ],
    },
  ];

  const visibleCapabilities = allCapabilities.filter(cap =>
    [
      'regulatory-classification',
      'regulatory-timelines',
      'regulatory-requirements',
    ].includes(cap.id)
  );

  const selectedCapability = allCapabilities.find(
    cap => cap.id === selectedCapabilityId
  );

  const handleCapabilityClick = (capabilityId: string) => {
    setSelectedCapabilityId(prev =>
      prev === capabilityId ? null : capabilityId
    );
  };

  const handleDismiss = (_id: string) => {
    setSelectedCapabilityId(null);
  };

  return (
    <FlexCol className="w-full max-w-full" gap="4">
      {!selectedCapability && (
        <div className="flex justify-center space-x-2 ">
          {visibleCapabilities.map(capability => {
            const Icon = capability.icon;
            const isSelected = selectedCapabilityId === capability.id;
            return (
              <button
                key={capability.id}
                onClick={e => {
                  e.preventDefault();
                  handleCapabilityClick(capability.id);
                }}
                type="button"
                className={cn(
                  'max-w-fit flex items-center gap-2 px-4 py-1.5 flex-1',
                  'rounded-lg border border-neutral-200',
                  'hover:bg-neutral-50 transition-colors',
                  isSelected ? 'bg-neutral-50 border-neutral-300' : 'bg-white'
                )}
              >
                <Icon className="w-5 h-5 text-neutral-600" />
                <Text variant="body-xs" color="primary">
                  {capability.title}
                </Text>
              </button>
            );
          })}
        </div>
      )}

      {/* Display selected capability card */}
      {selectedCapability && (
        <CapabilityCard
          capability={{
            ...selectedCapability,
            onDismiss: handleDismiss,
          }}
          onQueryClick={onQueryClick}
        />
      )}

      {/* Expandable capabilities section */}
      <div className="w-full">
        <button
          onClick={() => {
            setIsMoreExpanded(!isMoreExpanded);
            setSelectedCapabilityId(null);
          }}
          type="button"
          className="flex items-center gap-2 py-2 hover:bg-neutral-50 px-2 rounded transition-colors w-full justify-center"
        >
          <Text variant="body-sm-bold" color="primary">
            Explore More Capabilities
          </Text>
          <ChevronDown
            className={cn(
              'w-4 h-4 transition-transform duration-200',
              isMoreExpanded && 'rotate-180'
            )}
          />
        </button>

        {isMoreExpanded && (
          <FlexCol gap="3" className="mt-2">
            {allCapabilities.map(capability => (
              <CapabilityCard
                key={capability.id}
                capability={capability}
                onQueryClick={onQueryClick}
              />
            ))}
          </FlexCol>
        )}
      </div>
    </FlexCol>
  );
};
